import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { Calendar, RefreshCw, CheckCircle, XCircle, Clock } from 'lucide-react';

const T = {
  surface:'#ffffff', border:'#e8e4f3', borderL:'#f5f3ff',
  purple:'#7c3aed', purpleBg:'#ede9fe', purpleText:'#6d28d9',
  text:'#1a1035', textMd:'#4a3f6b', textSm:'#8b7fb0', textXs:'#b8b3cc',
  green:'#059669', greenBg:'#d1fae5',
  red:'#dc2626', redBg:'#fee2e2',
  yellow:'#d97706', yellowBg:'#fef3c7',
  pink:'#db2777', pinkBg:'#fce7f3',
  font:"'Poppins', sans-serif",
  shadow:'0 1px 4px rgba(124,58,237,0.06)',
};

const fmt = n => String(n).padStart(2,'0');
const fmtTime = iso => {
  if (!iso) return '—';
  const d = new Date(iso);
  const h = d.getHours(), m = d.getMinutes();
  return `${fmt(h > 12 ? h - 12 : h || 12)}:${fmt(m)} ${h >= 12 ? 'PM' : 'AM'}`;
};
const fmtDur = mins => {
  if (!mins) return '—';
  return `${Math.floor(mins / 60)}h ${fmt(mins % 60)}m`;
};

const STATUS_CFG = {
  present:     { bg: T.greenBg,  text: T.green,  label: 'Present' },
  absent:      { bg: T.redBg,    text: T.red,    label: 'Absent' },
  leave:       { bg: T.purpleBg, text: T.purple, label: 'On Leave' },
  permission:  { bg: T.pinkBg,   text: T.pink,   label: 'Permission' },
  holiday:     { bg: T.yellowBg, text: T.yellow, label: 'Holiday' },
  half_day:    { bg: '#ccfbf1',   text: '#0d9488',label: 'Half Day' },
  regularized: { bg: '#dbeafe',   text: '#2563eb',label: 'Regularized' },
  weekend:     { bg: '#f3f4f6',   text: '#6b7280',label: 'Weekend' },
};

export default function StaffAttendanceView() {
  const today = new Date().toISOString().split('T')[0];
  const [selectedDate, setSelectedDate] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (date) => {
    setLoading(true);
    try {
      // Reuse admin today endpoint for today, monthly for past dates
      const isToday = date === today;
      if (isToday) {
        const r = await api.get('/attendance/admin/today');
        setData({ type: 'today', overview: r.data.overview || [], counts: r.data.counts || {} });
      } else {
        const [y, m] = date.split('-');
        const r = await api.get('/attendance/admin/monthly', {
          params: { month: parseInt(m), year: parseInt(y) }
        });
        // Filter to selected date only
        const dayRecords = (r.data || []).filter(rec => rec.date === date);
        setData({ type: 'date', records: dayRecords, date });
      }
    } catch {
      toast.error('Failed to load attendance');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(selectedDate); }, [selectedDate]);

  const displayDate = new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-IN', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
  });

  const isToday = selectedDate === today;

  // Build rows for display
  const rows = data?.type === 'today'
    ? (data.overview || []).map(emp => ({
        name: emp.name,
        role: emp.role,
        login: emp.attendance?.login_time,
        logout: emp.attendance?.logout_time,
        duration: emp.attendance?.duration_minutes,
        status: emp.attendance?.status || 'absent',
      }))
    : (data?.records || []).map(rec => ({
        name: rec.user?.name,
        role: rec.user?.role,
        login: rec.login_time,
        logout: rec.logout_time,
        duration: rec.duration_minutes,
        status: rec.status,
      }));

  const counts = data?.type === 'today' ? data.counts : {
    present: rows.filter(r => r.status === 'present' || r.status === 'half_day').length,
    absent: rows.filter(r => r.status === 'absent').length,
    onLeave: rows.filter(r => r.status === 'leave').length,
    onPermission: rows.filter(r => r.status === 'permission').length,
  };

  return (
    <div style={{ fontFamily: T.font }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:22, flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 style={{ fontWeight:700, fontSize:22, color:T.text, marginBottom:3 }}>Staff Attendance</h1>
          <p style={{ fontWeight:400, fontSize:13, color:T.textSm }}>{displayDate}</p>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          {/* Date picker */}
          <div style={{ display:'flex', alignItems:'center', gap:8, background:T.surface, border:`1px solid ${T.border}`, borderRadius:9, padding:'8px 13px' }}>
            <Calendar size={14} color={T.purple}/>
            <input type="date" value={selectedDate} max={today}
              onChange={e => setSelectedDate(e.target.value)}
              style={{ border:'none', background:'transparent', outline:'none', fontSize:13, fontFamily:T.font, color:T.text, cursor:'pointer' }}/>
          </div>
          {/* Today button */}
          {!isToday && (
            <button onClick={() => setSelectedDate(today)}
              style={{ background:T.purpleBg, border:`1px solid #c4b5fd`, color:T.purpleText, cursor:'pointer', borderRadius:9, padding:'8px 13px', fontSize:12, fontWeight:600, fontFamily:T.font }}>
              Today
            </button>
          )}
          <button onClick={() => load(selectedDate)}
            style={{ background:T.surface, border:`1px solid ${T.border}`, color:T.textSm, cursor:'pointer', borderRadius:9, padding:'8px 13px', fontSize:12, fontFamily:T.font, display:'flex', alignItems:'center', gap:5 }}>
            <RefreshCw size={13}/>Refresh
          </button>
        </div>
      </div>

      {/* Count cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))', gap:12, marginBottom:22 }}>
        {[
          { label:'Present', value:counts.present||0, bg:T.greenBg, color:T.green, I:CheckCircle },
          { label:'Absent', value:counts.absent||0, bg:T.redBg, color:T.red, I:XCircle },
          { label:'On Leave', value:counts.onLeave||0, bg:T.purpleBg, color:T.purple, I:Calendar },
          { label:'Permission', value:counts.onPermission||0, bg:'#fce7f3', color:'#db2777', I:Clock },
        ].map(({ label, value, bg, color, I }) => (
          <div key={label} style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:12, padding:'14px 16px', boxShadow:T.shadow }}>
            <div style={{ display:'flex', alignItems:'center', gap:9 }}>
              <div style={{ width:34, height:34, borderRadius:9, background:bg, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <I size={16} color={color}/>
              </div>
              <div>
                <div style={{ fontWeight:800, fontSize:22, color:T.text, lineHeight:1 }}>{value}</div>
                <div style={{ fontWeight:500, fontSize:11, color:T.textSm, marginTop:3 }}>{label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Staff table */}
      <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:14, overflow:'hidden', boxShadow:T.shadow }}>
        {/* Table header */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 130px 130px 100px 100px', padding:'11px 20px', background:'#faf9ff', borderBottom:`1px solid ${T.borderL}` }}>
          {['Employee', 'Login Time', 'Logout Time', 'Duration', 'Status'].map(h => (
            <span key={h} style={{ fontWeight:600, fontSize:11, color:T.textSm, textTransform:'uppercase', letterSpacing:0.8 }}>{h}</span>
          ))}
        </div>

        {loading ? (
          <div style={{ display:'flex', justifyContent:'center', padding:40 }}>
            <div style={{ width:24, height:24, border:'3px solid #ede9fe', borderTop:`3px solid ${T.purple}`, borderRadius:'50%', animation:'spin 0.8s linear infinite' }}/>
          </div>
        ) : rows.length === 0 ? (
          <div style={{ padding:36, textAlign:'center', color:T.textSm, fontSize:14 }}>
            No attendance records for this date
          </div>
        ) : (
          rows.map((row, idx) => {
            const cfg = STATUS_CFG[row.status] || STATUS_CFG.absent;
            return (
              <div key={idx} style={{ display:'grid', gridTemplateColumns:'1fr 130px 130px 100px 100px', padding:'13px 20px', borderBottom: idx < rows.length - 1 ? `1px solid ${T.borderL}` : 'none', alignItems:'center' }}>
                {/* Employee */}
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <div style={{ width:34, height:34, borderRadius:9, background:T.purpleBg, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:13, color:T.purpleText, flexShrink:0 }}>
                    {row.name?.[0]?.toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontWeight:600, fontSize:14, color:T.text }}>{row.name}</div>
                    <div style={{ fontWeight:400, fontSize:11, color:T.textSm, textTransform:'capitalize' }}>{row.role}</div>
                  </div>
                </div>
                <span style={{ fontWeight:400, fontSize:13, color:T.textMd }}>{fmtTime(row.login)}</span>
                <span style={{ fontWeight:400, fontSize:13, color:T.textMd }}>{fmtTime(row.logout)}</span>
                <span style={{ fontWeight:400, fontSize:13, color:T.textMd }}>{fmtDur(row.duration)}</span>
                <span style={{ background:cfg.bg, color:cfg.text, fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:20, whiteSpace:'nowrap', display:'inline-block' }}>
                  {cfg.label}
                </span>
              </div>
            );
          })
        )}
      </div>

      {/* Read-only note */}
      <div style={{ marginTop:14, padding:'9px 14px', background:'#f5f3ff', border:`1px solid #e8e4f3`, borderRadius:9, fontSize:12, color:T.textSm, textAlign:'center' }}>
        View only — contact admin for any attendance corrections
      </div>
    </div>
  );
}